import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GeometryPadWidgetConfig } from '../geometrypad.model';

/**
 * Right triangle with a data table showing exact sin, cos, tan values.
 * Hovering a row reshapes the triangle to match that angle via UPDATE_PARAM.
 *
 * Triangle has hypotenuse c = 10, right angle at C (gamma = 90°).
 * Changing `alpha` via UPDATE_PARAM reshapes the triangle.
 *
 * Exact values:
 *   30°: sin = 1/2,    cos = √3/2, tan = 1/√3
 *   45°: sin = √2/2,   cos = √2/2, tan = 1
 *   60°: sin = √3/2,   cos = 1/2,  tan = √3
 */
export const TRIG_TABLE_PRESET: GeometryPadWidgetConfig = {
  id: 'trig-table',
  type: 'geometrypad',
  params: {
    elements: {
      t1: {
        type: 'Triangle',
        params: { c: 10, alpha: 30, gamma: 90 },
        names: { a: 'opp', b: 'adj', c: 'hyp', alpha: 'θ' },
      },
    },
  },
  display: {
    geometry: {
      shape: { coloring: true },
      angle: { rightAngleDisplay: 'square', coloring: false },
      label: { names: 'tiered', values: 'given', coloring: true },
    },
  },
  behavior: {
    controls: [
      {
        id: 'trig-values',
        type: WIDGET_CONTROL_TYPE.DataTable,
        label: 'Trigonometric Ratios',
        position: 'right',
        columns: [
          { key: 'angle', header: '$\\theta$', align: 'center' },
          { key: 'sin', header: '$\\sin\\theta$', align: 'center' },
          { key: 'cos', header: '$\\cos\\theta$', align: 'center' },
          { key: 'tan', header: '$\\tan\\theta$', align: 'center' },
        ],
        rows: [
          {
            values: { angle: '$0°$', sin: '$0$', cos: '$1$', tan: '$0$' },
            onHover: {
              action: 'UPDATE_PARAM',
              payload: { param: 't1.alpha', value: 1 },
              transition: { duration: 400, easing: 'easeInOut', type: 'numeric' },
            },
          },
          {
            values: {
              angle: '$30°$',
              sin: '$\\frac{1}{2}$',
              cos: '$\\frac{\\sqrt{3}}{2}$',
              tan: '$\\frac{1}{\\sqrt{3}}$',
            },
            onHover: {
              action: 'UPDATE_PARAM',
              payload: { param: 't1.alpha', value: 30 },
              transition: { duration: 400, easing: 'easeInOut', type: 'numeric' },
            },
          },
          {
            values: { angle: '$45°$', sin: '$\\frac{\\sqrt{2}}{2}$', cos: '$\\frac{\\sqrt{2}}{2}$', tan: '$1$' },
            onHover: {
              action: 'UPDATE_PARAM',
              payload: { param: 't1.alpha', value: 45 },
              transition: { duration: 400, easing: 'easeInOut', type: 'numeric' },
            },
          },
          {
            values: { angle: '$60°$', sin: '$\\frac{\\sqrt{3}}{2}$', cos: '$\\frac{1}{2}$', tan: '$\\sqrt{3}$' },
            onHover: {
              action: 'UPDATE_PARAM',
              payload: { param: 't1.alpha', value: 60 },
              transition: { duration: 400, easing: 'easeInOut', type: 'numeric' },
            },
          },
          {
            values: { angle: '$90°$', sin: '$1$', cos: '$0$', tan: '$\\infty$' },
            onHover: {
              action: 'UPDATE_PARAM',
              payload: { param: 't1.alpha', value: 89 },
              transition: { duration: 400, easing: 'easeInOut', type: 'numeric' },
            },
          },
        ],
      },
    ],
  },
};
